import { AddressDetails, PAYMENT_TYPES } from '@vend/business-components'
import { ApiCustomer, ApiOutlet } from '@vend/types'
import { trackError } from '@vend/utilities'
import { VdFeatureManager, VdTracking } from '@vend/vend.js'
import DOMPurify from 'dompurify'
import Mustache from 'mustache'
import { add, multiply, vn } from 'vend-number'
import { getTForReceiptLocale } from '../../../managers/web-register-tx-manager'
import { GiftCardWithSourceId } from '../../gift-cards/gift-cards'

import {
  BARCODE_OPTIONS,
  BARCODE_OPTIONS_SALE_ID,
  CUSTOMER_DEFAULTS,
  CUSTOMER_WALKIN_CODE,
  GIFT_CARD_SKU,
  RECEIPT_DATE_FORMATS,
  RECEIPT_SETTINGS_HASH_DEFAULTS,
} from '../../../components/receipts/selected-receipts.constant'
import { customerAddress } from '../../../functions/customer'
import {
  getMainTenderForReceipt,
  getTotalTipAmount,
  hasCashDiscountingServiceFee,
  hasChange,
} from '../../../functions/payments'
import { RegisterManager } from '../../../managers/register-manager'

import {
  FEATURE_BARCODE_FROM_SALE_ID,
  FEATURE_SURCHARGE_TRANSLATION,
  FEATURE_XMEN2565_RECEIPT_WEB_PRESENCE,
  LRX_39407_CHEQUE_REFERENCE_READ,
} from '../../../constants/wr-features'
import {
  buildWebPresenceItems,
  WebPresenceItem,
} from '../../../services/web-presence-service'
import { displayRound } from '../../../filters/display-round'
import { dateFormat } from '../../../filters/wr-date-format'
import { isKlarnaPaymentType } from '../../../functions/payment-types/klarna'
import {
  getChangeAmountForDisplay,
  getFormattedReceiptNumberDetails,
  getOutstandingBalanceAfterChange,
  encodeSaleId,
  getSurchargeLabel,
  hasValidCustomerWithEmail,
  isClosed,
  isMarkedAsUnfulfilled,
  isParked,
  isReturn,
} from '../../../functions/sale'
import { addSearchParams } from '../../../functions/url-utils'
import { CustomerBalancesManager } from '../../../managers/customer-balances-manager'
import { LoyaltyManager } from '../../../managers/loyalty-manager'
import { RetailerSettingsManager } from '../../../managers/retailer-settings-manager'
import { PaymentData, ReceiptData } from '../../../managers/sale-manager'
import { FulfilmentType } from '../../../models/fulfilment'
import { Payment } from '../../../models/payment'
import { Sale } from '../../../models/sale'
import { $filter, $sce } from '../../../ng-compat/imports'
import { VD_COUNTRY_CODES } from '../../../vend-ui/constants'
import { calculateGiftCardExpiry } from '../../payments/calculate-gift-card-expiry'
import { TRANSACTION_STATUSES } from '../../payments/swiper-payment-modal/swiper-payment-modal-content.constant'
import { BarcodeService } from '../../sale/barcode/barcode-service'

const SERIAL_NUMBER_NAMES = new Set([
  'vend/serial-number',
  'vend/serial-numbers',
])

function isSerialNumberField(name: string): boolean {
  return SERIAL_NUMBER_NAMES.has(name.trim().toLowerCase())
}

/**
 * ReceiptPrintSelectedController is a controller for the receipt print selected component
 * that renders a receipt template using Mustache
 *
 * ***WARNING***: DO not try to get sale data from redux store, this is very prone to race condition where the sale data is clear on the store before the controller managed to get the sale data. This has caused 2 incidents
 * 1. https://app.incident.io/lightspeedhq/incidents/1238
 * 2. https://app.incident.io/lightspeedhq/incidents/474
 */
export class ReceiptPrintSelectedController {
  public renderedTemplate!: string
  public receiptCSS: any
  private receiptSettingsTemplate: any
  private customer: ApiCustomer | null | undefined
  private data: any
  private options: any
  private isStandardInvoice = false
  private isGiftReceipt = false
  private vdCurrencyFilter = $filter<(n: number | string) => string>(
    'vdCurrency'
  )

  private isTaxInclusive = true
  private wrMakeAbsolute = $filter<(n: number | string) => number>(
    'wrMakeAbsolute'
  )

  constructor(
    private readonly vdTracking: VdTracking,
    private readonly barcodeService: BarcodeService,
    private readonly wrUserNameFilter: any,
    private readonly retailerSettingsManager: RetailerSettingsManager,
    private readonly registerManager: RegisterManager,
    private readonly loyaltyManager: LoyaltyManager,
    private readonly customerBalancesManager: CustomerBalancesManager,
    private readonly vdFeatureManager: VdFeatureManager
  ) {
    'ngInject'
  }

  public $onInit() {
    // Convert to any because the AngularJS types are missing trustAsCss.
    this.receiptCSS = ($sce as any).trustAsCss(
      `@media print { ${this.options.styling} }`
    )
    try {
      this.renderedTemplate = this.createMustacheReceipt()
    } catch (error) {
      trackError(
        '[ReceiptPrintSelectedControlller] Error creating receipt template',
        { data: { error } }
      )
    }

    this.trackEvents(this.vdTracking, this.data, this.receiptSettingsTemplate)
  }

  /**
   * Track user events with VdTracking, based on calculated options
   * and receipt settings for the rendered template
   */
  private trackEvents(
    tracker: VdTracking,
    data: Record<string, any>,
    receiptSettings: Record<string, any>
  ): void {
    if (receiptSettings.DisplayLoyaltyQRCode) {
      tracker.trackUserEvent({
        eventName: 'receipt.render.settings.display_loyalty_qrcode',
        metadata: {
          saleId: data.sale.localId,
        },
      })
    }
  }

  /**
   * createMustacheReceipt creates the template payload and renders the template using
   * the template html and payload.
   */
  private createMustacheReceipt() {
    this.createReceiptHash(this.data, this.options)
    const renderedTemplate = Mustache.render(
      this.options.templateHtml,
      this.receiptSettingsTemplate
    )
    if (!renderedTemplate) {
      throw new Error('mustache rendered template is empty')
    }
    return renderedTemplate
  }

  /**
   * Creates a mustache template
   * @param payload
   * @param templateInfo
   */
  private createReceiptHash(payload: ReceiptData, templateInfo: any) {
    const {
      sale,
      outlet,
      register,
      giftCardsInfo,
      receiptHtmlExtra,
      trainingModeEnabled,
      paymentData,
    } = payload

    const { settings, default_type: templateType } = templateInfo
    const hasCashDiscounting = this.registerManager.hasCashDiscounting()

    this.customer = sale.customer
    this.isStandardInvoice = templateType === 'a4'
    this.isGiftReceipt = !!templateInfo.isGiftReceipt
    this.isTaxInclusive = outlet.display_prices === 'inclusive'

    // The date format for the new A4 invoice template is different to that of thermal receipts
    const saleDateFormat = RECEIPT_DATE_FORMATS.SALE_DATE
    const saleHasCashDiscountingServiceFee = hasCashDiscountingServiceFee(
      hasCashDiscounting,
      sale
    )
    const displaySettings = this.setDisplaySettings(settings)

    const lspCashDiscountAmount = sale.totals.lspCashDiscount ?? 0

    this.receiptSettingsTemplate = {
      ...displaySettings,
      Customer: this.setCustomer(this.customer, sale.customerBalances),
      DateTime: dateFormat(sale.saleDate, saleDateFormat, outlet.time_zone),
      DisplayCashDiscount:
        saleHasCashDiscountingServiceFee &&
        vn(sale.totals.cashDiscount).isNeg(),
      DisplayServiceFee: saleHasCashDiscountingServiceFee,
      DisplayLSPCashDiscount: !vn(lspCashDiscountAmount).eq(0),
      InvoiceNumber: getFormattedReceiptNumberDetails(sale),
      IsParked: isParked(sale.status),
      OutletName: outlet.name,
      ReceiptHTMLExtra: receiptHtmlExtra,
      RegisterName: register.name,
      RegisterShowNotes: register.print_note_on_receipt,
      Totals: this.setTotals(sale, displaySettings),
      UserDisplayName: this.wrUserNameFilter(sale.user),
      IsInTrainingMode: trainingModeEnabled,
      IsSameBillingAddress: false,
    }

    // Customer
    if (this.receiptSettingsTemplate.DisplayCustomerDetails) {
      this.setCustomerDetailsDisplayValues(
        this.receiptSettingsTemplate.Customer,
        this.retailerSettingsManager.isStoreCreditEnabled(),
        displaySettings
      )

      if (this.receiptSettingsTemplate.DisplayStoreCredit) {
        // Calculate Adjusted Store Credit Balance
        const storeCredit = this.customerBalancesManager.calculateStoreCreditBalance()
        this.receiptSettingsTemplate.Customer.FormattedStoreCredit = this.vdCurrencyFilter(
          storeCredit
        )
      }
    }

    // List Items
    const { TotalSavings } = this.setItems(
      sale,
      outlet,
      register,
      giftCardsInfo,
      this.receiptSettingsTemplate.DisplayTaxPerLineItem
    )

    let totalSavings = TotalSavings

    // Sale Discount
    if (sale.discount) {
      const { TotalSavings } = this.setSaleDiscount(
        sale,
        displaySettings,
        totalSavings
      )
      totalSavings = TotalSavings
    }

    // Include LSP cash discount in total savings (value is negative, so negate it)
    // Not using abs() because returns carry a negative discount amount and gt(0) is used
    // as a guard to render the total savings section in the receipt template.
    if (!vn(lspCashDiscountAmount).eq(0)) {
      totalSavings = vn(add(totalSavings, vn(lspCashDiscountAmount).neg()))
    }

    // Format Total Savings
    if (totalSavings.gt(0)) {
      this.receiptSettingsTemplate.Totals.TotalSavings = this.vdCurrencyFilter(
        totalSavings.toNumber()
      )
    }

    // Tax Information
    const taxInformation = this.setTaxes(sale)

    // Set the conditional for whether the subtotal section should be displayed. It should only
    // be hidden if there is no discount AND no tax displayed on the receipt.
    if (sale.discount || this.receiptSettingsTemplate.DisplayTax) {
      this.receiptSettingsTemplate.DisplaySubtotal = true
    }

    // Get the total tip amount
    const tipAmount = getTotalTipAmount(sale.payments)

    if (vn(tipAmount).gt(0)) {
      this.receiptSettingsTemplate.DisplayTipAmount = true
      this.receiptSettingsTemplate.Totals.FormattedTipAmount = this.vdCurrencyFilter(
        tipAmount
      )
    }

    // Get total surcharge amount

    const surchargeAmount = sale.finals.surchargeDisplayProperties.surcharge

    if (!vn(surchargeAmount).equals(0)) {
      this.receiptSettingsTemplate.DisplaySurchargeAmount = true
      this.receiptSettingsTemplate.Totals.FormattedSurchargeAmount = this.vdCurrencyFilter(
        surchargeAmount
      )
      // Set surcharge label
      const surchargeLabel = this.vdFeatureManager.get(
        FEATURE_SURCHARGE_TRANSLATION
      )
        ? this.getTranslatedSurchargeLabel(sale)
        : getSurchargeLabel(sale)
      this.receiptSettingsTemplate.Totals.SurchargeAmountLabel = surchargeLabel
    }

    const ecomCustomCharges = sale.finals.ecomCustomCharges
    const ecomCustomChargesTotalIncl =
      sale.finals?.ecomCustomCharges?.totalIncl ?? 0

    if (ecomCustomCharges) {
      this.receiptSettingsTemplate.Totals.EcomCustomCharges = ecomCustomCharges.charges.map(
        charge => {
          const t = getTForReceiptLocale(this.options.locale)
          const chargeName = vn(charge.totalTax).eq(0)
            ? charge.name
            : t('{chargeName} (tax incl.)', {
                chargeName: charge.name,
                _context: 'Receipt ecom charge tax suffix',
              })
          return {
            Name: chargeName,
            FormattedTotal: this.vdCurrencyFilter(charge.totalIncl),
          }
        }
      )
    }

    // Create the payments for the sale
    const paymentInformation = this.setPayments(
      sale,
      outlet,
      taxInformation.TotalTax,
      giftCardsInfo,
      displaySettings,
      paymentData,
      tipAmount,
      surchargeAmount,
      lspCashDiscountAmount,
      ecomCustomChargesTotalIncl
    )

    // Combine tax and payment information into the mustache Totals object
    this.receiptSettingsTemplate.Totals = {
      ...this.receiptSettingsTemplate.Totals,
      ...paymentInformation,
      ...taxInformation,
    }

    // Sale Note
    if (register.print_note_on_receipt && sale.note) {
      this.receiptSettingsTemplate.Note = sale.note.replace(/\n/g, '<br>')
    }

    // Loyalty Information
    this.setLoyalty(
      sale,
      this.receiptSettingsTemplate.Customer,
      displaySettings
    )

    // Barcode - FF LRX-33927-barcode_from_sale_id to control whether to use localId or Invoice Number
    // Skip barcode entirely for sale-less ABP flow — there is no real sale to encode.
    if (sale.payAccountBalance?.abpTransactionId) {
      this.receiptSettingsTemplate.Barcode = ''
    } else if (this.vdFeatureManager.get(FEATURE_BARCODE_FROM_SALE_ID)) {
      this.receiptSettingsTemplate.Barcode = this.barcodeService.generateBarcodeImage(
        {
          value: encodeSaleId(sale.localId),
          options: BARCODE_OPTIONS_SALE_ID,
        }
      )
    } else {
      this.receiptSettingsTemplate.Barcode = this.barcodeService.generateBarcodeImage(
        {
          value: getFormattedReceiptNumberDetails(sale),
          options: BARCODE_OPTIONS,
        }
      )
    }

    // If an account balance payment was made during this sale
    // include the amount of the receipt.
    if (sale.payAccountBalance?.amount) {
      this.receiptSettingsTemplate.AccountBalancePayment = this.vdCurrencyFilter(
        sale.payAccountBalance.amount
      )
    }

    if (this.vdFeatureManager.get('custom_fields_on_receipt')) {
      this.setSaleCustomFields(sale)
    }

    if (
      this.vdFeatureManager.get(FEATURE_XMEN2565_RECEIPT_WEB_PRESENCE) &&
      !this.isGiftReceipt
    ) {
      this.setWebPresence(templateInfo, outlet)
    }
  }

  /**
   * setDisplaySettings sets display settings according to the sale details,
   * returns defaults if no settings.
   *
   * @param settings
   */
  private setDisplaySettings(settings: any) {
    if (!settings) {
      return RECEIPT_SETTINGS_HASH_DEFAULTS
    }

    return {
      ...settings,
      DisplayCustomerDetails:
        settings.DisplayCustomerDetails &&
        this.customer &&
        this.customer.customer_code !== CUSTOMER_WALKIN_CODE,
    }
  }

  /**
   * setCustomerDetailsDisplayValues sets the customers detail display values for the  receipt
   * @param customer - the customer from the receipt hash
   * @param storeCreditEnabled - a flag for whether the retailer has store credit enabled
   * @param displaySettings - the display settings for the receipt
   */
  private setCustomerDetailsDisplayValues(
    customer: any,
    storeCreditEnabled: boolean,
    displaySettings: any
  ) {
    this.receiptSettingsTemplate.DisplayCustomerCompany =
      customer.CompanyName && displaySettings.DisplayCustomerCompany
    this.receiptSettingsTemplate.DisplayCustomerName =
      (customer.FirstName || customer.LastName) &&
      displaySettings.DisplayCustomerName
    this.receiptSettingsTemplate.DisplayCustomerEmail =
      customer.Email && displaySettings.DisplayCustomerEmail
    this.receiptSettingsTemplate.DisplayCustomerPhone =
      (customer.Phone || customer.Mobile) &&
      displaySettings.DisplayCustomerPhone
    this.receiptSettingsTemplate.DisplayCustomerAddress =
      customer.FormattedPostalAddress && displaySettings.DisplayCustomerAddress
    this.receiptSettingsTemplate.DisplayStoreCredit =
      storeCreditEnabled &&
      !customer.WalkIn &&
      displaySettings.DisplayCustomerBalances
    this.receiptSettingsTemplate.DisplayCustomerCode =
      customer.CustomerCode && displaySettings.DisplayCustomerCode
  }

  /**
   * setTotals initialises the totals
   */
  private setTotals(sale: Sale, displaySettings: any) {
    const t = getTForReceiptLocale(this.options.locale)
    return {
      Change: {},
      SingleTax: false,
      MultipleTaxName: '',
      FormattedMultipleTaxAmount: '',
      FormattedServiceFee: this.vdCurrencyFilter(sale.totals.saleServiceFee),
      FormattedCashDiscount: this.vdCurrencyFilter(sale.totals.cashDiscount),
      FormattedLSPCashDiscount: this.vdCurrencyFilter(
        sale.totals.lspCashDiscount
      ),
      FormattedTotalWithServiceFee: this.vdCurrencyFilter(
        sale.getCashDiscountedSaleTotal()
      ),
      ItemCountLabel: t('items', { _context: 'Receipt item count label' }),
      TotalNumberOfItems: 0,
      TaxLabel: displaySettings.LabelTax,
      SubtotalLabel: displaySettings.LabelSubTotal,
      TipAmountLabel: displaySettings.LabelTipAmount,
      FormattedSubtotal: this.vdCurrencyFilter(sale.finals.subtotal),
      TotalLabel: displaySettings.LabelTotal,
      FormattedTotal: 0,
      Payments: [],
      GiftCards: [],
      TotalSavings: 0,
      ToPayLabel: displaySettings.LabelToPay,
    }
  }

  /**
   * setCustomer creates the customer object for the receipt hash,
   * returning default values if the customer is not defined
   *
   * @param customer
   */
  private setCustomer(
    customer: ApiCustomer | null | undefined,
    customerBalances: any
  ) {
    if (!customer) {
      return CUSTOMER_DEFAULTS
    }
    const postalAddress = DOMPurify.sanitize(this.setPostalAddress(customer))

    return {
      CompanyName: customer.company_name,
      CustomerCode: customer.customer_code,
      Email: customer.email,
      FirstName: customer.first_name,
      FormattedOnAccountBalance: this.vdCurrencyFilter(
        customerBalances && customerBalances.account
      ),
      FormattedPostalAddress: postalAddress,
      FormattedStoreCredit: '',
      HasCustomerContactDetails:
        customer.email ||
        customer.mobile ||
        customer.phone ||
        postalAddress.length > 0,
      LastName: customer.last_name,
      Mobile: customer.mobile,
      Phone: customer.phone,
      LoyaltyBalance: customer.loyalty_balance,
      WalkIn: customer.customer_code === CUSTOMER_WALKIN_CODE,
      LoyaltyEnabled: customer.enable_loyalty,
    }
  }

  /**
   * setLoyalty creates the loyalty object for the receipt template
   *
   * @param sale - a sale
   * @param customer - the customer in the  receipt hash
   * @param displaySettings - the display settings for the receipt
   */
  private setLoyalty(sale: Sale, customer: any, displaySettings: any) {
    const DisplayLoyaltySection =
      this.retailerSettingsManager.isLoyaltyEnabled() &&
      (customer.WalkIn || customer.LoyaltyEnabled)

    if (DisplayLoyaltySection) {
      const loyaltyClaimUrl = this.loyaltyManager.getLoyaltyClaimURL(sale)

      const qrcodeImageURL = this.loyaltyManager.getLoyaltyClaimQRCodeURL(
        addSearchParams(loyaltyClaimUrl, {
          source: 'qrcode',
        })
      )
      const loyaltyEarned =
        !isClosed(sale.status) && !isMarkedAsUnfulfilled(sale.status)
          ? 0
          : this.loyaltyManager.getTotalLoyalty(sale)

      const loyaltyNeededForRedemption = this.loyaltyManager.getCustomerLoyaltyThresholdRedemption()

      const loyaltyHash = {
        FormattedEarned: this.vdCurrencyFilter(loyaltyEarned),
        FormattedBalance: this.vdCurrencyFilter(
          this.loyaltyManager.getCustomerLoyaltyBalance(sale)
        ),
        CustomLoyaltyLink: loyaltyClaimUrl.href,
        QRCodeImageURL: qrcodeImageURL.href,
        LoyaltyNeededForRedemption: this.vdCurrencyFilter(
          loyaltyNeededForRedemption
        ),
      }
      const DisplayLoyaltyBalance =
        !customer.WalkIn &&
        displaySettings.DisplayCustomerBalances &&
        displaySettings.DisplayCustomerDetails &&
        customer.LoyaltyEnabled

      const retailerLoyaltySettings = this.loyaltyManager.getLoyaltySettings()

      const displayLoyaltyRedemption =
        loyaltyNeededForRedemption > 0 &&
        retailerLoyaltySettings?.thresholdSettings.enabled

      const DisplayLoyaltyLink =
        !hasValidCustomerWithEmail(sale.customer) &&
        displaySettings.DisplayLoyaltyLink

      if (DisplayLoyaltyLink) {
        const loyaltyClaimUrl = addSearchParams(
          new URL(this.loyaltyManager.getLoyaltyClaimURL(sale)),
          {
            source: 'qrcode',
          }
        )
        this.receiptSettingsTemplate.LoyaltyQRCode = this.loyaltyManager.getLoyaltyClaimQRCodeURL(
          loyaltyClaimUrl
        )
      }

      this.receiptSettingsTemplate.Loyalty = loyaltyHash
      this.receiptSettingsTemplate.DisplayLoyaltyLink = DisplayLoyaltyLink
      this.receiptSettingsTemplate.DisplayLoyaltyQRCode = DisplayLoyaltyLink
      this.receiptSettingsTemplate.DisplayLoyaltyBalance = DisplayLoyaltyBalance
      this.receiptSettingsTemplate.DisplayCustomerBalances =
        displaySettings.DisplayCustomerBalances &&
        !!customer.FormattedOnAccountBalance
      this.receiptSettingsTemplate.DisplayLoyaltyEarned =
        displaySettings.DisplayLoyaltyEarned && vn(loyaltyEarned).gt(0)
      this.receiptSettingsTemplate.DisplayLoyaltyRedemption = displayLoyaltyRedemption
    }
    this.receiptSettingsTemplate.DisplayLoyaltySection = DisplayLoyaltySection
  }

  /**
   * setPayments creates the payments object for the hash
   */
  private setPayments(
    sale: Sale,
    outlet: any,
    totalTax: any,
    giftCardsInfo: any,
    displaySettings: any,
    paymentData: PaymentData | undefined,
    tipAmount: number,
    surchargeAmount: string | number,
    lspCashDiscountAmount: string | number,
    ecomCustomChargesTotalIncl: string | number
  ) {
    if (paymentData && paymentData.isSignatureSlipReceipt === true) {
      const tender = paymentData.transaction
        ? getMainTenderForReceipt(paymentData.transaction.tenders)
        : null

      const formattedToPay = tender
        ? this.vdCurrencyFilter(tender.amount)
        : null

      const formattedDate = tender
        ? dateFormat(
            tender.created_at,
            RECEIPT_DATE_FORMATS.PAYMENT_DATE,
            outlet.time_zone
          )
        : null

      return {
        Payments: [
          {
            Name:
              paymentData.paymentType?.payment_type &&
              paymentData.paymentType.name !==
                paymentData.paymentType.payment_type.name
                ? paymentData.paymentType.name +
                  '<br>' +
                  paymentData.paymentType.payment_type.name
                : paymentData.paymentType?.name,
            FormattedAmount: formattedToPay,
            PaymentDate: formattedDate,
            Transaction: {
              Tenders: {
                ExternalTransactionId: tender?.external_transaction_id || null,
                CardDetails: {
                  Card: {
                    LastFour: tender?.card_details?.card?.last_four || null,
                  },
                },
                TerminalID: tender?.provider_fields.terminal_id || null,
                Signature:
                  tender?.provider_fields.signature_url ||
                  tender?.provider_fields.signature_base64 ||
                  null,
              },
            },
          },
        ],
        FormattedToPay: formattedToPay,
        FormattedTotal: formattedToPay,
      }
    }

    // Payments
    const t = getTForReceiptLocale(this.options.locale)
    const showPaymentReference = this.vdFeatureManager.get(
      LRX_39407_CHEQUE_REFERENCE_READ
    )
    let payments = sale.payments.map((payment: Payment) => {
      let signatureRequired
      switch (payment.transactionStatus) {
        case TRANSACTION_STATUSES.APPROVED_SIGNATURE:
          signatureRequired = t('Signature Required', {
            _context: 'Receipt payment signature status',
          })
          break
        case TRANSACTION_STATUSES.APPROVED_PIN:
          signatureRequired = t('Signature Not Required', {
            _context: 'Receipt payment signature status',
          })
          break
      }

      return {
        Name: payment.paymentType.name,
        FormattedAmount: this.vdCurrencyFilter(payment.amount),
        PaymentReference: showPaymentReference
          ? payment.paymentReference || ''
          : '',
        ExternalTransactionID: isKlarnaPaymentType(payment.paymentType.type_id)
          ? payment.external_transaction_id
          : '',
        PaymentDate: dateFormat(
          payment.paymentDate,
          RECEIPT_DATE_FORMATS.PAYMENT_DATE,
          outlet.time_zone
        ),
        PaymentTypeID: payment.paymentType.type_id,
        Transaction: this.getTransaction(
          payment,
          isReturn(sale),
          outlet.time_zone
        ),
        SignatureRequired: signatureRequired,
      }
    })

    let ChangeFormattedAmount = ''
    // Change
    if (hasChange(sale.change)) {
      this.receiptSettingsTemplate.LabelChange = displaySettings.LabelChange
        ? displaySettings.LabelChange
        : t('Change', { _context: 'Receipt payment change label' })
      ChangeFormattedAmount = this.vdCurrencyFilter(
        getChangeAmountForDisplay(sale)
      )
    }

    // Create the Gift Card payments for new receipts
    const GiftCards = this.setGiftCardPayments(giftCardsInfo, outlet)

    if (GiftCards) {
      /**
       * We need to remove the gift card payments from the list of payments
       * because gift card payments are displayed differently on new receipts
       */
      payments = payments.filter(
        (payment: { PaymentTypeID: number }) =>
          payment.PaymentTypeID !== PAYMENT_TYPES.GIFT_CARD &&
          payment.PaymentTypeID !== PAYMENT_TYPES.BIGCOMMERCE_GIFT_CERT
      )
    }

    const formattedToPay = this.vdCurrencyFilter(
      getOutstandingBalanceAfterChange(sale)
    )
    const formattedTotal = this.vdCurrencyFilter(
      add(
        vn(sale.finals.exclusivePrice),
        vn(totalTax),
        tipAmount,
        vn(surchargeAmount),
        vn(lspCashDiscountAmount),
        vn(ecomCustomChargesTotalIncl)
      )
    )

    return {
      GiftCards,
      ChangeFormattedAmount,
      Payments: payments,
      FormattedToPay: formattedToPay,
      FormattedTotal: formattedTotal,
      HasPayments: payments.length > 0,
    }
  }

  /**
   * Returns the translated entry method for a receipt entry method.
   * @param entryMethod - The entry method for the receipt.
   * @returns The translated entry method for the receipt entry.
   */
  private getEntryMethod(entryMethod?: string) {
    const t = getTForReceiptLocale(this.options.locale)
    switch (entryMethod) {
      // See https://github.com/vend/swiper/blob/master/internal/vend/transaction.go#L80-L88
      case 'SWIPED':
        return t('SWIPED', { _context: 'Receipt entry method' })
      case 'KEYED':
        return t('KEYED', { _context: 'Receipt entry method' })
      case 'CONTACTLESS':
        return t('CONTACTLESS', { _context: 'Receipt entry method' })
      case 'EMV':
        return t('EMV', { _context: 'Receipt entry method' })
      case 'UNKNOWN':
        return t('UNKNOWN', { _context: 'Receipt entry method' })
      case 'CHIP':
        return t('CHIP', { _context: 'Receipt entry method' })
      case 'FALLBACK':
        return t('FALLBACK', { _context: 'Receipt entry method' })
      case 'CONTACTLESSMSD':
        return t('CONTACTLESSMSD', { _context: 'Receipt entry method' })
      default:
        return entryMethod || null
    }
  }

  /**
   * Returns the translated card type.
   * @param cardType - The card type for the receipt.
   * @returns The translated card type for the receipt card type.
   */
  private getCardType(cardType?: string) {
    const t = getTForReceiptLocale(this.options.locale)
    switch (cardType) {
      // See https://github.com/vend/swiper/blob/master/internal/vend/transaction.go#L126-L130
      case 'CREDIT':
        return t('CREDIT', { _context: 'Receipt card type' })
      case 'DEBIT':
        return t('DEBIT', { _context: 'Receipt card type' })
      case 'EBTFOOD':
        return t('EBTFOOD', { _context: 'Receipt card type' })
      case 'EBTCASH':
        return t('EBTCASH', { _context: 'Receipt card type' })
      case 'GIFT':
        return t('GIFT', { _context: 'Receipt card type' })
      default:
        return cardType || null
    }
  }

  /**
   *
   * @param payment
   * @param isReturn
   * @param outletTimeZone
   */
  private getTransaction(
    payment: Payment,
    isReturn: boolean,
    outletTimeZone: any
  ) {
    const t = getTForReceiptLocale(this.options.locale)
    // If payment contains card, emv or Klarna return the tender with payment data.
    if (
      payment.card ||
      payment.emv ||
      isKlarnaPaymentType(payment.paymentType.type_id)
    ) {
      let surchargeTender = null
      if (
        payment.surchargeData?.amount &&
        !vn(payment.surchargeData.amount).isNegative()
      ) {
        // Check for surcharge and create a new tender for it to display it in the transaction details
        surchargeTender = {
          Type: t('Surcharge', { _context: 'Receipt payment surcharge' }),
          ExternalTransactionID: payment.external_transaction_id,
        }
      }

      return {
        Tenders: [
          {
            CreatedAt: dateFormat(
              payment.paymentDate,
              RECEIPT_DATE_FORMATS.PAYMENT_DATE,
              outletTimeZone
            ),
            CardDetails: payment.card
              ? {
                  EntryMethod: this.getEntryMethod(payment.card.entryMethod),
                  Card: {
                    Brand: payment.card.brand || null,
                    LastFour: payment.card.lastFour || null,
                    Type: this.getCardType(payment.card.type),
                  },
                }
              : null,
            Type:
              isReturn && vn(payment.amount).isNeg()
                ? t('REFUND', { _context: 'Receipt label' })
                : t('PURCHASE', { _context: 'Receipt label' }),
            ExternalTransactionID: payment.external_transaction_id,
            EMV: payment.emv
              ? {
                  Aid: payment.emv.aid || null,
                  ApplicationLabel:
                    payment.emv.applicationPreferredName ||
                    payment.emv.applicationLabel,
                  CryptogramType: payment.emv.cryptogramType,
                  Cryptogram: payment.emv.cryptogram,
                  Tags: payment.emv.tags
                    ? payment.emv.tags.map((tag: any) => {
                        return { Key: tag.key, Value: tag.value }
                      })
                    : null,
                }
              : null,
            Status: t('ACCEPTED', { _context: 'Receipt label' }),
            ProviderFields: payment.providerFields,
          },
          !!surchargeTender && {
            ...surchargeTender,
          },
        ],
      }
    }

    return null
  }

  private getTranslatedSurchargeLabel(sale: Sale): string {
    const t = getTForReceiptLocale(this.options.locale)
    // Tax included text should be shown when tax is NOT zero
    const showTaxIncl = !vn(sale.finals.tax).eq(0)
    const surchargeRules = this.getUniqueSurchargeRules(sale)
    const translationContext = 'Surcharge label in sale receipt preview'

    // No rules
    if (surchargeRules === '') {
      return showTaxIncl
        ? t('Payment surcharge (tax incl.)', { _context: translationContext })
        : t('Payment surcharge', { _context: translationContext })
    }

    // Single rule
    if (surchargeRules.length === 1) {
      return showTaxIncl
        ? t('Payment surcharge {rule} (tax incl.)', {
            rule: surchargeRules[0],
            _context: translationContext,
          })
        : t('Payment surcharge {rule}', {
            rule: surchargeRules[0],
            _context: translationContext,
          })
    }

    // Multiple rules
    return showTaxIncl
      ? t(
          '{ruleCount, plural, one {Payment surcharge {ruleCount} rate (tax incl.)} other {Payment surcharge {ruleCount} rates (tax incl.)}}',
          { ruleCount: surchargeRules.length, _context: translationContext }
        )
      : t(
          '{ruleCount, plural, one {Payment surcharge {ruleCount} rate} other {Payment surcharge {ruleCount} rates}}',
          { ruleCount: surchargeRules.length, _context: translationContext }
        )
  }

  private getUniqueSurchargeRules(sale: Sale): string[] | '' {
    if (!sale.finals.surchargeDisplayProperties?.surchargeRules?.length) {
      return ''
    }

    const { surchargeRules } = sale.finals.surchargeDisplayProperties
    const uniqueRules = [...new Set(surchargeRules)]

    if (uniqueRules.includes('')) {
      return ''
    }

    return uniqueRules
  }

  /**
   * setGiftCardPayments creates the gift card payment
   */
  private setGiftCardPayments(
    giftCardsInfo: {
      giftCardPaymentsSummary: any
    },
    outlet: {
      time_zone: any
    }
  ) {
    if (giftCardsInfo) {
      const giftCards = giftCardsInfo.giftCardPaymentsSummary
      if (giftCards) {
        return giftCards.map((giftCard: GiftCardWithSourceId) => {
          const transaction = giftCard.transactions.find((transaction: any) => {
            return transaction.id === giftCard.sourceId
          })
          if (transaction) {
            const expiry = giftCard.expiresAt
            const timeZone = outlet.time_zone
            return {
              ID: giftCard.id,
              Number: giftCard.code,
              FormattedExpiresAt: expiry
                ? dateFormat(expiry, RECEIPT_DATE_FORMATS.EXPIRY_DATE, timeZone)
                : '',
              FormattedTotalRedeemed: this.vdCurrencyFilter(
                this.wrMakeAbsolute(transaction.amount)
              ),
              TotalRedeemed: this.wrMakeAbsolute(transaction.amount),
              FormattedBalance: this.vdCurrencyFilter(giftCard.balance),
              PaymentDate: dateFormat(
                transaction.createdAt,
                RECEIPT_DATE_FORMATS.PAYMENT_DATE,
                timeZone
              ),
            }
          }
          return undefined
        })
      }
    }
    return null
  }

  /**
   * setTaxes sets the tax object for the hash
   */
  private setTaxes(sale: Sale) {
    const totalTax = sale.finals.tax
    let noTaxItems = 0
    const saleTaxes = sale.finals.taxes
    // Map each of the sale taxes to a mustache Tax object
    const taxes = Object.keys(saleTaxes).map(key => {
      const tax = saleTaxes[key]
      let taxRate = displayRound(multiply(tax.rate.rate, 100), 3, true) + '%'
      if (tax.rate.rate === 0) {
        taxRate = ''
        noTaxItems++
      }
      return {
        Name: tax.name,
        Rate: taxRate,
        FormattedTotalAmount:
          Object.keys(sale.finals.taxes).length === 1
            ? this.vdCurrencyFilter(totalTax)
            : this.vdCurrencyFilter(tax.total),
      }
    })
    let multipleTaxName = ''
    let formattedMultipleTaxAmount = ''

    // If there are zero tax taxes, we won't include them in the total number of taxes value
    const numberOfTaxes = taxes.length - noTaxItems

    // Multiple Taxes
    if (numberOfTaxes > 1) {
      const t = getTForReceiptLocale(this.options.locale)
      multipleTaxName = t('{numberOfTaxes} taxes', {
        numberOfTaxes,
        _context: 'Receipt tax summary label',
      })
      formattedMultipleTaxAmount = this.vdCurrencyFilter(totalTax)
    }

    this.receiptSettingsTemplate.DisplayTax =
      this.receiptSettingsTemplate.DisplayTax && numberOfTaxes > 0

    return {
      Taxes: taxes,
      TotalTax: totalTax,
      SingleTax: numberOfTaxes === 1,
      MultipleTaxName: multipleTaxName,
      FormattedMultipleTaxAmount: formattedMultipleTaxAmount,
    }
  }

  /**
   * calculateGiftCardExpiry calculates the expiry
   */
  private calculateGiftCardExpiry(giftCardsInfo: $TSFixMe, outlet: ApiOutlet) {
    const expiryPeriod = giftCardsInfo.giftCardSettings.expiry_period || ''
    const expiryPeriodType =
      giftCardsInfo.giftCardSettings.expiry_period_type || ''
    return calculateGiftCardExpiry(
      expiryPeriod,
      expiryPeriodType,
      outlet.time_zone
    )
  }

  /**
   * setSaleDiscount sets the sale discount for the sale
   */
  private setSaleDiscount(sale: Sale, displaySettings: any, totalSavings: any) {
    let dollarDiscount = sale.discount?.price || 0
    if (this.isTaxInclusive) {
      dollarDiscount = add(sale.discount?.price || 0, sale.discount?.tax || 0)
    }
    totalSavings = add(totalSavings, dollarDiscount || 0)
    this.receiptSettingsTemplate.Totals.OrderDiscountFormattedDollarDiscount = this.vdCurrencyFilter(
      multiply(dollarDiscount, -1)
    )
    return {
      TotalSavings: vn(totalSavings),
    }
  }

  /**
   * setPostalAddress is a function taken from the old createReceiptHash that
   * sets the customers postal address
   *
   * @param customer a raw customer
   */
  private setPostalAddress(customer: ApiCustomer): string {
    if (!this.isStandardInvoice) {
      return customerAddress(customer, 'postal')
    }

    const postalAddress = []

    const postal1 = customer.postal_address_1
      ? customer.postal_address_1.trim()
      : ''
    const postal2 = customer.postal_address_2
      ? customer.postal_address_2.trim()
      : ''
    const suburb = customer.postal_suburb ? customer.postal_suburb.trim() : ''
    const city = customer.postal_city ? customer.postal_city.trim() : ''
    const postCode = customer.postal_postcode
      ? customer.postal_postcode.trim()
      : ''
    const state = customer.postal_state ? customer.postal_state.trim() : ''
    const countryId = customer.postal_country_id
      ? customer.postal_country_id.trim()
      : ''

    if (postal1 && postal2) {
      postalAddress.push(`${postal1}, ${postal2}`)
    } else if (postal1) {
      postalAddress.push(postal1)
    } else if (postal2) {
      postalAddress.push(postal2)
    }

    if (suburb) {
      postalAddress.push(suburb)
    }

    if (city) {
      if (postCode) {
        postalAddress.push(`${city} ${postCode}`)
      } else {
        postalAddress.push(city)
      }
    } else {
      if (postCode) {
        postalAddress.push(postCode)
      }
    }

    if (state) {
      if (countryId) {
        postalAddress.push(`${state}, ${countryId}`)
      } else {
        postalAddress.push(state)
      }
    } else {
      if (countryId) {
        postalAddress.push(countryId)
      }
    }

    // In the new A4 invoice, the address is treated as HTML so we use a <br> element to separate each line
    return postalAddress.join('<br>')
  }

  /**
   * setItems creates the items for the hash
   */
  private setItems(
    sale: Sale,
    outlet: any,
    register: any,
    giftCardsInfo: any,
    displayTaxPerLineItem: boolean
  ) {
    const t = getTForReceiptLocale(this.options.locale)
    let totalNumberOfItems = vn(0)
    // totalSavings is a sum of the sale wide discount and line item discounts
    let totalSavings = vn(0)

    // Calculate the gift card expiry when the receipt is requested from the sell screen
    let calculatedGiftCardExpiry = ''
    if (giftCardsInfo && giftCardsInfo.giftCardSettings) {
      calculatedGiftCardExpiry = this.calculateGiftCardExpiry(
        giftCardsInfo,
        outlet
      )
    }
    const listItems = sale.lineItems.map(lineItem => {
      // Calculate gift card line item details
      let giftCardExpiry = ''
      const { giftCardNumber, giftCardExpiresAt } = lineItem
      if (giftCardNumber && giftCardExpiresAt) {
        giftCardExpiry = dateFormat(
          giftCardExpiresAt,
          RECEIPT_DATE_FORMATS.EXPIRY_DATE,
          outlet.time_zone
        )
      }
      // Calculate the total number of items on the sale
      totalNumberOfItems = vn(
        add(totalNumberOfItems, vn(lineItem.displayQuantity))
      )

      const totalCost = this.isTaxInclusive
        ? lineItem.totals.inclusivePrice
        : lineItem.totals.exclusivePrice

      const product = lineItem.product
      const productNotFoundText = t('Product not found', {
        _context: 'Receipt line item fallback',
      })
      const listItem: any = {
        ProductName:
          product && product.name ? product.name : productNotFoundText,
        // If the product is a gift card, the SKU should not be shown
        ProductSKU:
          product && product.sku && product.sku !== GIFT_CARD_SKU
            ? product.sku
            : '',
        ProductVariantName: product
          ? product.variant_name
          : productNotFoundText,
        ProductVariantInfo: '',
        ProductVariantOptions: product && product.variant_options,
        Quantity: lineItem.displayQuantity,
        FormattedCost: this.vdCurrencyFilter(lineItem.displayPrice),
        FormattedTotalCost: this.vdCurrencyFilter(totalCost),
        HasDiscount:
          vn(lineItem.displayDiscount).gt(0) &&
          register.show_discounts_on_receipts,
        ItemNote: lineItem.note,
        FormattedGiftCardExpiry: giftCardExpiry || calculatedGiftCardExpiry,
        GiftCardNumber: lineItem.giftCardNumber,
        Promotions: '',
        ItemDiscountFormattedDollarDiscount: '',
        ItemDiscountFormattedOriginalPrice: '',
        ItemDiscountFormattedPercentageDiscount: '',
        LineItemTaxString: '',
        FirstLineItemTax: '',
        FormattedLineItemTaxes: [],
        FulfilmentType: lineItem.fulfilmentType,
      }

      // Product Variant Info
      const variantOptions = listItem.ProductVariantOptions
      listItem.ProductVariantInfo = variantOptions
        .map((option: any) => option.value)
        .join(' / ')

      // Promotions
      if (lineItem.promotionDetails) {
        listItem.Promotions = lineItem.promotionDetails.map(
          (promotion: any) => {
            return {
              Name: promotion.name,
              PromoCode: promotion.promoCode,
            }
          }
        )
      }

      // Line Item Discount
      if (listItem.HasDiscount) {
        // Calculate the unit price * quantity for the line item discount
        const totalLineItemDiscount = multiply(
          lineItem.displayDiscount,
          vn(lineItem.quantity)
        )
        totalSavings = vn(add(totalSavings, totalLineItemDiscount))
        /**
         * The base price is the item unit price x the item quantity
         */
        const basePrice = this.isTaxInclusive
          ? lineItem.totals.inclusiveBasePrice
          : lineItem.totals.exclusiveBasePrice
        /**
         * The Original price shown on old receipts is the item unit price
         * The Original price shown on new receipts is the item unit price x the item quantity
         */
        const originalPrice = basePrice
        listItem.ItemDiscountFormattedOriginalPrice = this.vdCurrencyFilter(
          originalPrice
        )
        listItem.ItemDiscountFormattedPercentageDiscount =
          displayRound(lineItem.displayDiscountPercent, 2, true) + '%'
        listItem.ItemDiscountFormattedDollarDiscount = this.vdCurrencyFilter(
          lineItem.displayDiscount
        )
      }

      // Line Item Taxes
      if (displayTaxPerLineItem) {
        const taxRates = lineItem.taxInfo?.rates || []
        const lineItemTaxStrings = taxRates.map((taxRate: any) => {
          const name = taxRate.display_name
          const rate = displayRound(multiply(taxRate.rate, 100), 3, true) + '%'
          const formattedTotalAmount = this.vdCurrencyFilter(
            multiply(lineItem.totals.exclusivePrice, taxRate.rate)
          )
          return `${name} (${rate} / ${formattedTotalAmount})`
        })
        if (this.isStandardInvoice) {
          listItem.FirstLineItemTax = lineItemTaxStrings.shift()
          listItem.FormattedLineItemTaxes = lineItemTaxStrings.map(
            (taxString: any) => {
              return {
                Value: taxString,
              }
            }
          )
        } else {
          listItem.LineItemTaxString = lineItemTaxStrings.join(', ')
        }
      }

      if (this.vdFeatureManager.get('custom_fields_on_receipt')) {
        listItem.CustomFields = lineItem.customFields
          ?.filter(cf => cf.printOnReceipt && cf.value !== null)
          .filter(cf => !this.isGiftReceipt || isSerialNumberField(cf.name))
          .map(cf => {
            return {
              Title: cf.title,
              Value: cf.value!.toString(),
            }
          })
      }

      return listItem
    })

    this.receiptSettingsTemplate.ListDispatchItems = []
    this.receiptSettingsTemplate.ListPickupItems = []
    this.receiptSettingsTemplate.ListTakeHomeItems = []

    listItems.forEach((item: any) => {
      if (item.FulfilmentType === FulfilmentType.DELIVERY) {
        this.receiptSettingsTemplate.ListDispatchItems.push(item)
      } else if (item.FulfilmentType === FulfilmentType.PICKUP) {
        this.receiptSettingsTemplate.ListPickupItems.push(item)
      } else if (item.FulfilmentType === FulfilmentType.UNSPECIFIED) {
        this.receiptSettingsTemplate.ListTakeHomeItems.push(item)
      }
    })

    const displayListDispatchItems =
      this.receiptSettingsTemplate.ListDispatchItems.length > 0
    const displayListPickupItems =
      this.receiptSettingsTemplate.ListPickupItems.length > 0
    const displayListTakeHomeItems =
      (displayListDispatchItems || displayListPickupItems) &&
      this.receiptSettingsTemplate.ListTakeHomeItems.length > 0

    if (displayListDispatchItems) {
      this.receiptSettingsTemplate.DisplayListDispatchItems = true
      this.setFulfillmentAddress(sale)
      // For delivery sales with captured addresses, ensure addresses display
      // regardless of receipt display settings (per LRX-31790).
      this.overrideDisplayCustomerDetails()
    } else if (displayListPickupItems) {
      this.receiptSettingsTemplate.DisplayListPickupItems = true
    }
    if (displayListTakeHomeItems) {
      this.receiptSettingsTemplate.DisplayListTakeHomeItems = true
    } else if (!displayListDispatchItems && !displayListPickupItems) {
      this.receiptSettingsTemplate.ListItems = listItems
    }

    this.receiptSettingsTemplate.Totals.TotalNumberOfItems = totalNumberOfItems
    this.receiptSettingsTemplate.Totals.ItemCountLabel = t(
      '{count, plural, one {item} other {items}}',
      {
        count: Math.abs(Number(totalNumberOfItems)),
        _context: 'Receipt item count label',
      }
    )

    return { TotalSavings: totalSavings }
  }

  private setSaleCustomFields(sale: Sale) {
    this.receiptSettingsTemplate.CustomFields = sale.customFields
      ?.filter(cf => cf.printOnReceipt && cf.value !== null)
      .map(cf => {
        if (cf.imageURI) {
          return {
            Title: cf.title,
            ImageURL: cf.imageURI,
          }
        }

        return {
          Title: cf.title,
          Value: cf.value!.toString(),
        }
      })
  }

  private setFulfillmentAddress(sale: Sale) {
    if (sale.fulfilmentDetails.length === 0) {
      return
    }

    const shippingAddress = sale.fulfilmentDetails?.[0].shippingAddress
    const billingAddress = sale.fulfilmentDetails?.[0].billingAddress

    // Set shipping address if it exists
    if (shippingAddress) {
      this.receiptSettingsTemplate.ShippingAddress = this.formatAddress(
        shippingAddress
      )
    }

    // Set billing address if it exists
    if (billingAddress) {
      this.receiptSettingsTemplate.BillingAddress = this.formatAddress(
        billingAddress
      )
    }

    // IsSameBillingAddress - true only when BOTH exist AND match
    this.receiptSettingsTemplate.IsSameBillingAddress =
      shippingAddress != null &&
      billingAddress != null &&
      this.areAddressesEqual(shippingAddress, billingAddress)
  }

  private overrideDisplayCustomerDetails() {
    const shippingLine1 = this.receiptSettingsTemplate.ShippingAddress
      ?.AddressLine1
    const billingLine1 = this.receiptSettingsTemplate.BillingAddress
      ?.AddressLine1
    if (
      this.receiptSettingsTemplate.DisplayListDispatchItems &&
      ((shippingLine1 !== undefined && shippingLine1 !== '') ||
        (billingLine1 !== undefined && billingLine1 !== ''))
    ) {
      this.receiptSettingsTemplate.DisplayCustomerDetails = true
      this.receiptSettingsTemplate.Customer.HasCustomerContactDetails = true
      this.receiptSettingsTemplate.DisplayCustomerCompany =
        this.customer?.company_name && this.customer.company_name !== ''
      this.receiptSettingsTemplate.DisplayCustomerName = !!(
        this.customer?.first_name || this.customer?.last_name
      )
      this.receiptSettingsTemplate.DisplayCustomerAddress = true
      this.receiptSettingsTemplate.Customer.FormattedPostalAddress = this
        .customer
        ? DOMPurify.sanitize(this.setPostalAddress(this.customer))
        : CUSTOMER_DEFAULTS.FormattedPostalAddress
    }
  }

  private areAddressesEqual(
    address1: Partial<AddressDetails>,
    address2: Partial<AddressDetails>
  ): boolean {
    const keys: Array<keyof AddressDetails> = [
      'address_1',
      'address_2',
      'suburb',
      'city',
      'state',
      'country_id',
      'postcode',
    ]
    return keys.every(key => (address1[key] || '') === (address2[key] || ''))
  }

  private formatAddress(
    address: Partial<AddressDetails>
  ): {
    AddressLine1: string
    AddressLine2: string
  } {
    const line1 = [address.address_1, address.address_2]
      .filter(Boolean)
      .join(', ')
    let line2 = [address.city, address.postcode, address.state]
      .filter(Boolean)
      .join(' ')

    let country_name = VD_COUNTRY_CODES.find(
      country => country.code === address.country_id
    )?.name
    country_name = country_name || `${address.country_id}`

    line2 = line2.trim() ? `${line2}, ${country_name}` : country_name

    return {
      AddressLine1: line1,
      AddressLine2: line2,
    }
  }

  private setWebPresence(templateInfo: any, outlet: ApiOutlet): void {
    const showWebPresence = templateInfo.show_web_presence
    const webPresenceItems: WebPresenceItem[] = templateInfo.web_presence ?? []
    const enabledItems = webPresenceItems.filter(
      item => item.is_enabled !== false
    )

    if (!showWebPresence || enabledItems.length === 0) {
      return
    }

    this.receiptSettingsTemplate.DisplayWebPresence = true
    this.receiptSettingsTemplate.WebPresenceItems = buildWebPresenceItems(
      enabledItems
    )
  }
}
